import React, { useEffect, useRef } from "react"
import { usePageContext } from "vike-react/usePageContext"
import Button from "aldoo-ra/Button"
import Img from "../ui/Img"
import Spinner from "../ui/Spinner"
import PriceDisplay from "../ui/PriceDisplay"
import useT from "../hooks/useT"
import useAsync from "../hooks/useAsync"
import useSiteLanguage, { useCurrency } from "../hooks/useSiteLanguage"
import { usePageTitle } from "../hooks/usePageTitle"
import { stripLang, apiPid, buildContentUrl } from "../utils/contentRoutes"
import { normalizeProduct } from "../utils/productUtils"
import { LoadProduct, PRODUCT_FIELDS } from "../api"
import { itemUnitPrice } from "../store/cart"
import { openCheckout } from "../store/checkout"
import Typography from "../../Variants/Typography"

/** /buy-book/:pid — direct single-product checkout (opens the checkout with just this item). */
export default function BuyProductPage() {
  const t = useT()
  const language = useSiteLanguage()
  const currency = useCurrency()
  const pageContext = usePageContext()
  const pid = stripLang(pageContext.urlPathname).split("/").filter(Boolean)[1]
  const { result, pending } = useAsync(() => LoadProduct({ pid: apiPid(decodeURIComponent(pid || "")), language, currency, fields: PRODUCT_FIELDS }), [pid, language, currency], { enabled: !!pid })
  const product = result?.pid ? normalizeProduct(result) : null
  const opened = useRef(false)
  usePageTitle(product ? `${t`Buy`} ${product.name}` : t`Buy`)

  const open = () => product && openCheckout([{ product, quantity: 1 }])

  useEffect(() => {
    if (!product || opened.current) return
    opened.current = true
    open()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.pid])

  if (pending && !result) return <Spinner className="py-24" />
  if (!product) return <div className="py-24 text-center text-text_secondary">{t`Product not found`}</div>

  return (
    <div className="w-full max-w-[720px] mx-auto px-4 pt-10 pb-24 flex flex-col items-center gap-6 text-center" data-testid="buy-book">
      <Img 
        src={product.square} 
        alt={product.name} 
        width={200} 
        height={200} 
        className="w-[200px] h-[200px] rounded-lg overflow-hidden" /
      >

      <Typography 
        component="h1"
        variant="h2"
      >
        {product.name}
      </Typography>

      <PriceDisplay amount={itemUnitPrice(product)} currency={product.real_money_currency || currency} variant="large" />
      
      <Button variant="primary_l" onClick={open}>
        {t`Buy`}
      </Button>
      <Button variant="link_dim" href={buildContentUrl(product)}>
        {t`Back to the book`}
      </Button>
    </div>
  )
}
